/** @format */

import { useState, useEffect, lazy } from "react";
import dynamic from "next/dynamic";
import Head from "next/head";
import Script from "next/script";
import { SSRKeycloakProvider, SSRCookies } from "@react-keycloak/ssr";
import { enquireScreen } from "enquire-js";
import "antd/dist/reset.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "../styles/globals.css";

import UserHeader from "../components/UserHeader/UserHeader";
const SubHeader = lazy(() => import("../components/SubHeader/SubHeader"));
const Footer = lazy(() => import("../components/Footer/Footer"));
import ErrorBoundary from "./ErrorBoundary";

import { Provider } from "react-redux";
import { parse as cookie_parse } from "cookie";
import store from "../store";
import { useRouter } from "next/router";

const ScrollToTop = dynamic(
  () => import("../components/ScrollToTop/ScrollToTop"),
  { ssr: false }
);

const keycloakConfig = {
  url: process.env.NEXT_PUBLIC_REACT_APP_KEYCLOAK_URL,
  realm: process.env.NEXT_PUBLIC_REACT_APP_KEYCLOAK_REALM,
  clientId: process.env.NEXT_PUBLIC_REACT_APP_KEYCLOAK_CLIENT_ID,
};

const initOptions = {
  onLoad: "check-sso",
  checkLoginIframe: false,
  silentCheckSsoRedirectUri:
    process.env.NEXT_PUBLIC_URL + "/silent-check-sso.html",
};

function MyApp({ Component, pageProps, cookies }) {
  let url = false;
  const [isMobile, setIsMobile] = useState(false);
  const [hideHeader, setHideHeader] = useState(false);
  const [isShowRibbon, setShowRibbon] = useState(!url);
  const [hideHeaderFooter, setHideHeaderFooter] = useState(false);
  const [showFooter, setShowFooter] = useState(false);
  const [showSubHeader, setShowSubHeader] = useState(false);

  const router = useRouter();
  const canonicalUrl = `https://www.qalara.com${
    router.asPath?.split("?")[0] || "/"
  }`;

  const pathname = router?.pathname ?? "/";

  url =
    pathname.includes("cart") ||
    pathname.includes("payment-success") ||
    pathname.includes("notfound") ||
    pathname.includes("shipping") ||
    pathname.includes("signup") ||
    pathname.includes("payment");

  let subheaderBanner =
    pathname.includes("payment-success") ||
    pathname.includes("payment-failure") ||
    pathname.includes("notfound") ||
    pathname.includes("signup") ||
    pathname.includes("account");

  useEffect(() => {
    enquireScreen((status) => setIsMobile(status));

    if ("serviceWorker" in navigator) {
      window.addEventListener("load", function () {
        navigator.serviceWorker.register("/serviceworker.js");
      });
    }

    setTimeout(() => setShowSubHeader(true), 1000);
  }, []);

  useEffect(() => {
    if (
      router.pathname === "/nynowspecials" ||
      router.pathname === "/qalara-dropship"
    ) {
      setHideHeaderFooter(true);
    } else {
      setHideHeaderFooter(false);
    }
  }, [router.pathname]);

  useEffect(() => {
    const handleScroll = () => setShowFooter(true);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const pathsForHideHeader = [
    "/signin",
    "/signup",
    "/forgotpassword",
    "/resetpassword/[kcid]",
    "/setpassword/[kcid]",
  ];

  const pathsForSubheader = [
    "/",
    "/QBazaar",
    "/explore/curatedbyus",
    "/explore/home-furnishing",
    "/explore/Fashion-store",
    "/explore/shopnow",
  ];

  return (
    <Provider store={store}>
      <>
        {/* ✅ SEO */}
        <Head>
          <link rel="canonical" href={canonicalUrl} />
        </Head>

        {/* ✅ Breadcrumb Schema */}
        <Script
          id="qalara-breadcrumb-schema"
          type="application/ld+json"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Home",
                  item: "https://www.qalara.com/",
                },
              ],
            }),
          }}
        />

        {/* ✅ GTM (FIXED) */}
       <Script
  id="gtm"
  strategy="afterInteractive"
  dangerouslySetInnerHTML={{
    __html: `
      (function(w,d,s,l,i){
        w[l]=w[l]||[];
        w[l].push({'gtm.start': new Date().getTime(), event:'gtm.js'});
        var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),
        dl=l!='dataLayer'?'&l='+l:'';
        j.async=true;
        j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
        f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','GTM-KTVSR8R');
    `,
  }}
/>

        {/* ✅ CookieHub */}
        <Script id="cookiehub" strategy="afterInteractive">
          {`
            (function (h, u, b) {
              var d = h.getElementsByTagName("script")[0],
              e = h.createElement("script");
              e.async = true;
              e.src = "https://cookiehub.net/c2/c50d9e7b.js";
              e.onload = function () {
                u.cookiehub.load(b);
              };
              d.parentNode.insertBefore(e, d);
            })(document, window, {});
          `}
        </Script>

        {/* ✅ Clarity (FIXED) */}
        <Script
          id="clarity"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: `
              (function(c,l,a,r,i,t,y){
                c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                t=l.createElement(r);t.async=1;
                t.src="https://www.clarity.ms/tag/"+i;
                y=l.getElementsByTagName(r)[0];
                y.parentNode.insertBefore(t,y);
              })(window, document, "clarity", "script", "o61x47fc5l");
            `,
          }}
        />

        {/* ✅ Tawk */}
        {/* <Script
          strategy="lazyOnload"
          src="https://embed.tawk.to/5fdb39afdf060f156a8df4ae/1epo5ilog"
        /> */}

        {/* ✅ Picreel (FIXED) */}
        {/* <Script
          src="https://app.picreel.com/assets/main.js"
          strategy="lazyOnload"
          data-picreel="cUZaWXFHdVBGN3IyVWhtb3FUbXRmdz09"
        /> */}

        <ErrorBoundary>
          {!hideHeaderFooter &&
            !pathsForHideHeader.includes(router?.pathname) && (
              <UserHeader isShowRibbon={isShowRibbon} />
            )}

          <ScrollToTop>
            <main
              className={
                isShowRibbon
                  ? "ribbon-visible main-layout-next"
                  : "main-layout-next"
              }
            >
              <Component {...pageProps} isShowRibbon={isShowRibbon} />
            </main>
          </ScrollToTop>

          {!hideHeaderFooter && showFooter && <Footer />}

          {!hideHeaderFooter &&
            showSubHeader &&
            isMobile &&
            pathsForSubheader.includes(router?.pathname) && (
              <div style={{ position: "sticky", bottom: "0px", zIndex: "1" }}>
                <SubHeader hideSubheaderBanner={subheaderBanner} />
              </div>
            )}
        </ErrorBoundary>
      </>
    </Provider>
  );
}

/* ✅ KEEP cookies (no change) */
function parseCookies(request) {
  if (!request || !request.headers) return {};
  return cookie_parse(request.headers.cookie || "");
}

MyApp.getInitialProps = async ({ ctx }) => {
  const { req } = ctx || {};
  return {
    cookies: parseCookies(req),
  };
};

export default MyApp;